import crypto from 'crypto-js';
import { USER_DATA_FIELDS } from './constants';

/**
 * Hash data using SHA-256
 * @param {string} data - Data to hash
 * @returns {string|null} - Hashed data or null if no data provided
 */
export const hashData = (data) => {
    if (!data) return null;
    return crypto.SHA256(data.toString()).toString();
};

/**
 * Get Facebook Browser ID (_fbp)
 * Format: fb.1.1554763741205.1838349632
 * @returns {string|null} - Facebook Browser ID
 */
export const getFacebookBrowserId = () => {
    try {
        const cookies = document.cookie.split(';');
        const fbpCookie = cookies.find(c => c.trim().startsWith('_fbp='));
        if (!fbpCookie) {
            // Generate new fbp if not exists
            const version = '1';
            const subdomainIndex = '1';
            const creationTime = Date.now();
            const randomNumber = Math.floor(Math.random() * 2147483647);
            const fbp = `fb.${version}.${creationTime}.${randomNumber}`;
            document.cookie = `_fbp=${fbp}; path=/; max-age=7776000`; // 90 days
            return fbp;
        }
        return fbpCookie.split('=')[1];
    } catch (error) {
        console.error('Error getting Facebook Browser ID:', error);
        return null;
    }
};

/**
 * Get Facebook Click ID (_fbc)
 * Format: fb.1.1554763741205.AbCdEfGhIjKlMnOpQrStUvWxYz
 * @returns {string|null} - Facebook Click ID
 */
export const getFacebookClickId = () => {
    try {
        // First check URL parameters for fbclid
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');
        console.log("found fbclid in url params", fbclid)
        
        if (fbclid) {
            // If fbclid exists in URL, create new _fbc cookie
            const fbc = `fb.1.${Date.now()}.${fbclid}`;
            document.cookie = `_fbc=${fbc}; path=/; max-age=7776000`; // 90 days
            console.log("created new _fbc cookie", fbc)
            return fbc;
        }

        // If no fbclid in URL, check for existing _fbc cookie
        const cookies = document.cookie.split(';');
        const fbcCookie = cookies.find(c => c.trim().startsWith('_fbc='));
        console.log("found _fbc cookie", fbcCookie)
        return fbcCookie ? fbcCookie.split('=')[1] : null;
    } catch (error) {
        console.error('Error getting Facebook Click ID:', error);
        return null;
    }
};

/**
 * Get Facebook cookies (fbp and fbc)
 * @returns {Object} - Object containing fbp and fbc cookies
 */
export const getFacebookCookies = () => {
    return {
        fbp: getFacebookBrowserId(),
        fbc: getFacebookClickId()
    };
};

/**
 * Split name into first and last name
 * @param {string} name - Full name
 * @returns {Object} - Object containing first and last name
 */
export const splitName = (name) => {
    if (!name) return { firstName: '', lastName: '' };
    const nameParts = name.split(' ');
    return {
        firstName: nameParts[0] || '',
        lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : ''
    };
};

/**
 * Format date for Facebook CAPI
 * @param {Date} date - Date object
 * @returns {string|null} - Formatted date string or null
 */
export const formatDate = (date) => {
    if (!date) return null;
    return date instanceof Date ? date.toISOString().slice(0, 10).replace(/-/g, '') : null;
};

/**
 * Prepare user data with hashing for Facebook CAPI
 * @param {Object} userData - User data object
 * @returns {Object} - Prepared user data object
 */
export const prepareUserData = (userData = {}) => {
    // Get Facebook cookies (fbp and fbc)
    const { fbp, fbc } = getFacebookCookies();
    console.log("fbp and fbc", fbp, fbc)
    // Split name into first and last name if available
    const { firstName, lastName } = splitName(userData.name);
    
    // Get external ID from user data or localStorage
    const externalId = userData.id || localStorage.getItem('user_id');
    
    // Prepare the data object with proper hashing where required
    const hashedData = {
        // Required to hash
        [USER_DATA_FIELDS.EMAIL]: userData.email ? [hashData(userData.email)] : null,
        [USER_DATA_FIELDS.PHONE]: userData.phone ? [hashData(userData.phone)] : null,
        [USER_DATA_FIELDS.FIRST_NAME]: firstName ? [hashData(firstName)] : null,
        [USER_DATA_FIELDS.LAST_NAME]: lastName ? [hashData(lastName)] : null,
        [USER_DATA_FIELDS.DATE_OF_BIRTH]: userData.dob ? [hashData(formatDate(userData.dob))] : null,
        [USER_DATA_FIELDS.GENDER]: userData.gender ? [hashData(userData.gender)] : null,
        
        // Do NOT hash these fields (explicitly mentioned by FB)
        [USER_DATA_FIELDS.CLIENT_IP]: userData.clientIp || '',
        [USER_DATA_FIELDS.CLIENT_USER_AGENT]: navigator.userAgent,
        ...(fbp && { fbp }), // Browser ID - do not hash
        ...(fbc && { fbc }), // Click ID - do not hash
        ...(externalId && { external_id: externalId }), // External ID - do not hash
        
        // Include if available (not common in most web apps)
        ...(userData.fb_login_id && { fb_login_id: userData.fb_login_id })
    };

    // Remove null values
    return Object.fromEntries(Object.entries(hashedData).filter(([_, v]) => v !== null));
};

/**
 * Format order items for Facebook CAPI
 * @param {Array} items - Array of order items
 * @returns {Array} - Formatted items array
 */
export const formatOrderItems = (items = []) => {
    return items.map(item => ({
        id: item.subscription_plan?.subscription_plan_id || item.id || '',
        quantity: item.quantity || 1,
        item_price: item.cost || item.price || 0,
    }));
};

/**
 * Get current currency type
 * @returns {string} - Currency type (usd or inr)
 */
export const getCurrentCurrency = () => {
    return localStorage.getItem('current_currency_type')?.toLowerCase() || 'inr';
};

/**
 * Format event timestamp
 * @returns {number} - Unix timestamp in seconds
 */
export const getEventTimestamp = () => {
    return Math.floor(Date.now() / 1000);
};

/**
 * Get current page URL
 * @returns {string} - Current page URL
 */
export const getEventSourceUrl = () => {
    return window.location.href;
};

/**
 * Format error response
 * @param {Error} error - Error object
 * @param {string} eventName - Name of the event that failed
 * @returns {Object} - Formatted error object
 */
export const formatErrorResponse = (error, eventName) => {
    return {
        success: false,
        event: eventName,
        error: error.message,
        timestamp: new Date().toISOString()
    };
}; 